import type {SkillPathRowStatus, SkillPathStatusSegment} from './types';

/**
 * The status-scoped date aliases a list requests on the All tab, alongside its unscoped pair. Both
 * the skill-path and diagnostic queries name them identically, which is what lets the two share the
 * rule below.
 */
export interface ScopedDateAliases {
  openMinDueDate?: string | null;
  openMaxDueDate?: string | null;
  scheduledMinStartDate?: string | null;
  scheduledMaxStartDate?: string | null;
}

/** The subset of a row's dates a scoped range replaces. */
export interface ScopedDateOverride {
  minDueDate?: string | null;
  maxDueDate?: string | null;
  minStartDate?: string | null;
  maxStartDate?: string | null;
}

/**
 * The dates a row renders in place of its unscoped pair, or `null` to keep that pair.
 *
 * A status tab scopes every date to itself, so nothing is replaced there. All scopes none, and its
 * unscoped pair spans every recipient group — correct only for a closed row, whose roll-up means no
 * group is open or scheduled and therefore every group is closed. An open row would otherwise
 * inherit a closed group's expired due date, and a scheduled row a start date already in the past.
 *
 * Both teacher-home lists follow this rule, per the Row contents section of
 * `.claude/plans/skill-path-management/part-01-skill-paths-list/ac.md`: the date shown ranges over
 * the in-scope groups matching the row's status. Shared so the two cannot drift apart.
 */
export const scopedDateOverride = (
  row: ScopedDateAliases,
  segment: SkillPathStatusSegment,
  status: SkillPathRowStatus
): ScopedDateOverride | null => {
  if (segment !== 'all' || status === 'closed') {
    return null;
  }
  return status === 'scheduled'
    ? {
        minStartDate: row.scheduledMinStartDate ?? null,
        maxStartDate: row.scheduledMaxStartDate ?? null
      }
    : {minDueDate: row.openMinDueDate ?? null, maxDueDate: row.openMaxDueDate ?? null};
};
